import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent, useCallback, useEffect, useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {TEXT_FIELD_BACKGROUND_COLOR, TEXT_FIELD_BORDER} from 'chemistry/molecules/MoleculeConstants';
import {MonomerCapGroup, MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import {
  SingleMonomerQuery,
  SingleMonomerQueryVariables,
} from 'molbio/monomers/__generated/SingleMonomerQuery';
import MonomerConfigureAttachmentPointsSection from 'molbio/monomers/admin/MonomerConfigureAttachmentPointsSection';
import MonomerStructureAndWeightConfigurationPane from 'molbio/monomers/admin/MonomerStructureAndWeightConfigurationPane';
import {gqlToAPINaturalAnalogue} from 'molbio/monomers/MonomersUtil';
import notifications from 'notifications';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {InputErrorMessageStyle, LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup from 'ui-library/ToggleGroup';

import {
  MonomerCapGroupWithWildcard,
  WILDCARD_CAP_GROUP_SYMBOL,
  SINGLE_MONOMER_QUERY,
} from '../MonomerConstants';
import {
  MonomerValidationInput,
  MonomerValidationErrors,
  validateMonomerInput,
  MonomerUpdate,
  updateMonomer as updateMonomerHelper,
} from '../MonomersDAO';
import MonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from './MonomerConfigurationPageContainer';

export interface MonomerManualEditPageProps {
  monomerId: string;
}

export interface MonomerManualEditPageContentsProps {
  monomerId: string;
  initialName: string;
  initialSmilesInput: string;
  initialCustomMolecularWeight: number | null;
  initialSymbol: string;
  // The following fields can't be edited currently, but are sent over as part of the validation process
  naturalAnalogue: MonomerNaturalAnalogue;
}

export function MonomerManualEditConfirmationModal(props: {
  onRequestClose: () => void;
  updateMonomer: () => void;
}): JSX.Element {
  return (
    <Modal
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          onClick: () => {
            props.updateMonomer();
            props.onRequestClose();
          },
          text: 'Yes, save monomer',
        },
      ]}
      title="Edit monomer structure?"
    >
      <p>
        If this monomer is used in a complex polymer, editing the structure may invalidate registered complex
        polymers and remove existing conjugate attachments. Are you sure you want to continue editing the
        structure?
      </p>
    </Modal>
  );
}

function MonomerManualEditPageContents({
  monomerId,
  initialName,
  initialSmilesInput,
  initialCustomMolecularWeight,
  initialSymbol,
  naturalAnalogue,
}: MonomerManualEditPageContentsProps): JSX.Element {
  const [smilesInput, setSmilesInput] = useState(initialSmilesInput);
  const [canonicalSmiles, setCanonicalSmiles] = useState<string | null>(null);
  const [customMolecularWeight, setCustomMolecularWeight] = useState<string | null>(
    initialCustomMolecularWeight?.toFixed(3) ?? null
  );
  const [calculatedMolecularWeight, setCalculatedMolecularWeight] = useState<number | null>(null);
  const [editingMolecularWeight, setEditingMolecularWeight] = useState(false);
  const [attachmentPointsInput, setAttachmentPointsInput] = useState<Array<MonomerCapGroup | null> | null>(
    null
  );
  const [calculatedAttachmentPoints, setCalculatedAttachmentPoints] =
    useState<Array<MonomerCapGroupWithWildcard> | null>([]);
  const [name, setName] = useState(initialName);
  const [symbol, setSymbol] = useState(initialSymbol);
  const [structureImage, setStructureImage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<MonomerValidationErrors>({
    smiles: null,
    symbol: null,
    attachmentPoints: null,
    molecularWeight: null,
    structureImage: null,
    naturalAnalogue: null,
    customMolecularWeight: null,
  });
  const [isValid, setIsValid] = useState(false);
  const [editingSmiles, setEditingSmiles] = useState(true);
  const [validationInFlight, setValidationInFlight] = useState(false);
  const [saveInFlight, setSaveInFlight] = useState(false);
  const [hasCustomWeightError, setHasCustomWeightError] = useState(false);

  const hasChanges =
    name !== initialName ||
    smilesInput !== initialSmilesInput ||
    customMolecularWeight !== initialCustomMolecularWeight ||
    symbol !== initialSymbol;

  const doValidateAndSetValues = useCallback(
    async (
      monomerValidationInput: MonomerValidationInput,
      syncSmilesInputWithCanonicalSmiles: boolean
    ): Promise<void> => {
      setValidationInFlight(true);
      const outputs = await validateMonomerInput([monomerValidationInput]);
      assert(outputs.length === 1, 'Expected exactly one monomer validation output');
      const {properties, errors, isValid} = outputs[0];
      setCanonicalSmiles(properties.canonicalSmiles);
      setCalculatedAttachmentPoints(properties.attachmentPoints);
      setAttachmentPointsInput(
        properties.attachmentPoints?.map((attachmentPoint) =>
          attachmentPoint !== WILDCARD_CAP_GROUP_SYMBOL ? attachmentPoint : null
        ) ?? null
      );
      setCalculatedMolecularWeight(properties.molecularWeight);
      setStructureImage(properties.structureImage);

      setValidationErrors(errors);
      setIsValid(isValid);
      if (syncSmilesInputWithCanonicalSmiles && properties.canonicalSmiles != null) {
        setSmilesInput(properties.canonicalSmiles);
      }
      setValidationInFlight(false);
    },
    []
  );

  // Called upon first render to populate calculated values from initial SMILES
  useEffect(() => {
    const monomerValidationInput: MonomerValidationInput = {
      name: initialName,
      smiles: initialSmilesInput,
      customMolecularWeight: initialCustomMolecularWeight,
      symbol: initialSymbol,
      naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue),
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, false);
  }, [
    initialSmilesInput,
    initialName,
    initialCustomMolecularWeight,
    initialSymbol,
    naturalAnalogue,
    doValidateAndSetValues,
    monomerId,
  ]);

  const onSmilesInputChange = (newSmilesInput: string): void => {
    setSmilesInput(newSmilesInput);
    const monomerValidationInput: MonomerValidationInput = {
      name,
      smiles: newSmilesInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol,
      naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue),
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, false);
  };

  const onAttachmentPointsInputChange = (
    newAttachmentPointsInput: Array<MonomerCapGroup | null> | null
  ): void => {
    const monomerValidationInput: MonomerValidationInput = {
      name,
      smiles: smilesInput,
      capGroups: newAttachmentPointsInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol,
      naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue),
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, true);
  };

  const onNameChange = (newName: string): void => {
    setName(newName);
    const monomerValidationInput: MonomerValidationInput = {
      name: newName,
      smiles: smilesInput,
      capGroups: attachmentPointsInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol,
      naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue),
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, false);
  };

  const onSymbolChange = (newSymbol: string): void => {
    setSymbol(newSymbol);
    const monomerValidationInput: MonomerValidationInput = {
      name,
      smiles: smilesInput,
      capGroups: attachmentPointsInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol: newSymbol,
      naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue),
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, false);
  };

  const updateMonomer = async (): Promise<void> => {
    assert(
      [name, symbol].every((field) => field != null),
      'Unexpected null input fields received for monomer update.'
    );
    const input: MonomerUpdate = {
      name,
      smiles: smilesInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol,
    };

    try {
      setSaveInFlight(true);
      await updateMonomerHelper(monomerId, input);
      notifications.success('Saved monomer');
      navigateToURL('/monomers');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    } finally {
      setSaveInFlight(false);
    }
  };

  const smilesInputFieldError = validationErrors.smiles ?? validationErrors.attachmentPoints;

  return (
    <div
      className={css(styles.pageContainer)}
      {...getTestProps({component: 'MonomerManualEditPage', element: 'root'})}
    >
      <MonomerConfigurationPageContainer purpose={MONOMER_CONFIGURATION_PURPOSE.EDIT} />
      <div className={css(styles.middleSectionContainer)}>
        <div className={css(styles.monomerPropertiesPane)}>
          <div className={css(styles.paneContainer)}>
            <TextField
              className={css(styles.marginBottom16)}
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onNameChange(e.target.value);
              }}
              label="Name"
              labelStyle={LABEL_STYLES.LABEL}
              isRequired={true}
              autoFocus={true}
              dataTestAttrs={{
                component: 'MonomerManualEditPage',
                element: 'nameInput',
              }}
            />
            <TextField
              className={css(styles.marginBottom16)}
              value={symbol}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onSymbolChange(e.target.value);
              }}
              label="Symbol"
              labelStyle={LABEL_STYLES.LABEL}
              isRequired={true}
              hasError={validationErrors.symbol != null}
              error={
                validationErrors.symbol != null
                  ? {
                      message: validationErrors.symbol,
                      style: InputErrorMessageStyle.INFO,
                    }
                  : null
              }
              dataTestAttrs={{
                component: 'MonomerManualEditPage',
                element: 'symbolInput',
              }}
            />
            <div className={css(styles.canonicalSmiles)}>
              <div className={css(styles.textFieldSubheader)}>
                <span>Canonical SMILES</span>
              </div>
              <div className={css(styles.grayTextFieldContainer)}>
                <span className={css(styles.grayTextField)}>{canonicalSmiles}</span>
              </div>
            </div>
            <div className={css(styles.marginBottom16)}>
              <ToggleGroup
                size={ButtonSize.MEDIUM}
                options={['Edit SMILES', 'Edit capping groups'].map((text) => ({
                  id: text,
                  buttonProps: {text},
                }))}
                selection={editingSmiles ? 'Edit SMILES' : 'Edit capping groups'}
                onChangeSelection={(selection: string) => {
                  setEditingSmiles(selection === 'Edit SMILES');
                }}
                isFullWidth={true}
              />
            </div>
            {editingSmiles ? (
              <TextField
                className={css(styles.inputSmilesField)}
                value={smilesInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onSmilesInputChange(e.target.value);
                }}
                label="SMILES"
                placeholder="Enter SMILES notation"
                autoFocus={true}
                hasError={smilesInputFieldError != null}
                error={
                  smilesInputFieldError != null
                    ? {
                        message: smilesInputFieldError,
                        style: InputErrorMessageStyle.INFO,
                      }
                    : null
                }
                dataTestAttrs={{
                  component: 'MonomerManualEditPage',
                  element: 'smilesInput',
                }}
              />
            ) : (
              <MonomerConfigureAttachmentPointsSection
                attachmentPointsInput={attachmentPointsInput}
                onChangeAttachmentPointsInput={onAttachmentPointsInputChange}
                calculatedAttachmentPoints={calculatedAttachmentPoints}
                allowNonWildcardCapGroupSelection={true}
              />
            )}
          </div>
        </div>
        <MonomerStructureAndWeightConfigurationPane
          smilesInput={smilesInput}
          customMolecularWeight={customMolecularWeight}
          calculatedMolecularWeight={calculatedMolecularWeight}
          onChangeCustomMolecularWeight={(weight: string | null) => {
            setCustomMolecularWeight(weight);
          }}
          validationErrors={validationErrors}
          editingMolecularWeight={editingMolecularWeight}
          onChangeEditingMolecularWeight={setEditingMolecularWeight}
          structureImage={structureImage}
          hasCustomWeightError={hasCustomWeightError}
          onChangeHasCustomWeightError={setHasCustomWeightError}
        />
      </div>
      <div className={css(styles.submitBar)}>
        <div className={css(styles.buttonsContainer)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={() => {
              navigateToURL('/monomers');
            }}
            size={ButtonSize.MEDIUM}
            text="Cancel"
          />
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            text={saveInFlight ? 'Saving...' : 'Save monomer'}
            size={ButtonSize.MEDIUM}
            disabled={
              saveInFlight ||
              validationInFlight ||
              !isValid ||
              editingMolecularWeight ||
              name.length === 0 ||
              hasCustomWeightError ||
              !hasChanges
            }
            onClick={() =>
              smilesInput !== initialSmilesInput && BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES
                ? legacyRunModal(MonomerManualEditConfirmationModal, {updateMonomer})
                : updateMonomer()
            }
            dataTestAttrs={{
              component: 'MonomerManualEditPage',
              element: 'saveMonomerButton',
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function MonomerManualEditPage({monomerId}: MonomerManualEditPageProps): JSX.Element {
  return (
    <BenchlingQuery<SingleMonomerQuery, SingleMonomerQueryVariables>
      query={SINGLE_MONOMER_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      renderCachedDataWhenRefetching={true}
      variables={{id: monomerId}}
    >
      {({monomer}) => {
        if (monomer.__typename === 'MonomerNode') {
          if (!monomer.isNatural) {
            return (
              <MonomerManualEditPageContents
                monomerId={monomerId}
                initialName={monomer.name}
                initialCustomMolecularWeight={monomer.customMolecularWeight}
                initialSmilesInput={monomer.originalSmiles}
                initialSymbol={monomer.symbol}
                naturalAnalogue={monomer.naturalAnalogue}
              />
            );
          } else {
            notifications.info('Cannot edit a natural monomer');
          }
        }
        navigateToURL('/monomers');
        return null;
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  middleSectionContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  monomerPropertiesPane: {
    width: '50%',
    borderRight: `1px solid ${GRAY_LIGHT}`,
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    padding: 16,
    gap: 10,
    boxShadow: '0px -2px 4px rgba(92, 124, 131, 0.1)',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  marginBottom16: {
    marginBottom: 16,
  },
  paneContainer: {
    marginLeft: 40,
    marginRight: 40,
    marginTop: 20,
  },
  inputSmilesField: {
    marginBottom: 12,
  },
  textFieldSubheader: {
    fontWeight: 'bold',
    marginBottom: 4,
    ...FONT_PROXIMA_13,
  },
  grayTextFieldContainer: {
    display: 'flex',
  },
  grayTextField: {
    ...FONT_PROXIMA_13,
    padding: '3px 10px',
    background: TEXT_FIELD_BACKGROUND_COLOR,
    border: TEXT_FIELD_BORDER,
    boxSizing: 'border-box',
    borderRadius: '4px',
    overflowWrap: 'anywhere',
    minHeight: 28,
    width: '100%',
  },
  canonicalSmiles: {
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: `1px solid ${GRAY}`,
  },
});
